import { put, select, takeLatest } from 'redux-saga/effects'
import { setComments, saveVitals } from '../store/vitals.actions'
import { VitalsConstants } from '../store/vitals.constants'
import { IPatientVitals } from '../store/vitals.types'
import { getPatientVitalsSelector } from '../selectors/patientVitals.selector'
import { getVitalOptionsSelector } from '../../options/selectors'
import { IVitalOptionsState } from '../../options/store/options.types'
import { IWithdrawalScale } from '../../options/store/options.types'
import { updateCommentBlock, removeWithdrawalScaleCommentBlock } from '../utils/vitals.util'

function getWithdrawalScalePrefix(vitals: IPatientVitals, vitalOptions: IVitalOptionsState): string | null {
  const { withdrawalScaleType } = vitals
  if (!withdrawalScaleType?.id) return null

  const withdrawalScaleOptions = (vitalOptions.withdrawalScaleOptions || []) as IWithdrawalScale[]
  const scaleDefinition = withdrawalScaleOptions.find((s) => s.scaleId === withdrawalScaleType.id)

  const scaleName = (scaleDefinition?.scaleName ?? withdrawalScaleType.name)?.replace(/\n/g, ' ')

  if (!scaleName) return null
  return `${scaleName} Score:`
}

const upsertWithdrawalScaleComment = (
  comments: string,
  wsBlock: string,
  prefix: string,
  scaleNames: string[]
): string => {
  const baseComments = removeWithdrawalScaleCommentBlock(comments, scaleNames)
  if (!wsBlock) {
    return baseComments
  }
  return updateCommentBlock(baseComments, wsBlock, prefix)
}

function buildWithdrawalScaleLine(vitals: IPatientVitals, vitalOptions: IVitalOptionsState): string {
  const { withdrawalScaleTotal, withdrawalScaleTotalPNNV, withdrawalScaleCriteria = [] } = vitals

  const prefix = getWithdrawalScalePrefix(vitals, vitalOptions)
  if (!prefix) return ''

  const selections = withdrawalScaleCriteria
    .map((s) => `${s.question?.replace(/\n/g, ' ')}: ${s.name?.replace(/\n/g, ' ')}`)
    .join(', ')

  const total = withdrawalScaleTotalPNNV?.name ?? withdrawalScaleTotal ?? 'N/A'
  return selections ? `${prefix} ${selections}, Total: ${total};` : `${prefix} Total: ${total};`
}

export function* setWithdrawalScaleComments() {
  const vitals: IPatientVitals = yield select(getPatientVitalsSelector)
  const vitalOptions: IVitalOptionsState = yield select(getVitalOptionsSelector)

  const prefix = getWithdrawalScalePrefix(vitals, vitalOptions)

  const withdrawalScaleNames = [
    ...((vitalOptions.withdrawalScaleOptions || []) as IWithdrawalScale[]).map((s) => s.scaleName),
    ...(vitalOptions.withdrawScaleTypeOptions || []).map((o) => o.name),
  ].filter(Boolean) as string[]

  const hasWithdrawalScaleData =
    !!vitals.withdrawalScaleType?.id ||
    !!vitals.withdrawalScaleCriteria?.length ||
    !!vitals.withdrawalScaleTotal ||
    !!vitals.withdrawalScaleTotalPNNV

  const wsBlock = hasWithdrawalScaleData && prefix ? buildWithdrawalScaleLine(vitals, vitalOptions) : ''

  const updated = upsertWithdrawalScaleComment(vitals.comments ?? '', wsBlock, prefix ?? '', withdrawalScaleNames)

  if (updated !== (vitals.comments ?? '')) {
    yield put(setComments(updated))
  }
  yield put(saveVitals())
}

export function* setWithdrawalScaleCommentsSaga() {
  yield takeLatest(
    [
      VitalsConstants.SET_WITHDRAWAL_SCALES,
      VitalsConstants.SET_WITHDRAWAL_SCALES_TOTAL,
      VitalsConstants.SET_WITHDRAWAL_SCALES_TOTAL_PNNV,
      VitalsConstants.SET_WITHDRAWAL_SCALE_TYPE,
    ],
    setWithdrawalScaleComments
  )
}
